import { Ship, Anchor, AlertTriangle, LifeBuoy, Download, RotateCcw } from 'lucide-react';
import type { UseSpeedBoatLiveSessionResult } from '../../../hooks/useSpeedBoatLiveSession';
import type { GameEvent, SpeedBoatConfig, SpeedBoatFactorType } from '../../../types/collaborativeGame';

interface SpeedBoatLiveResultsViewProps {
  game: UseSpeedBoatLiveSessionResult;
  onReset: () => void;
}

const TYPE_CONFIG = {
  engine:    { label: 'Engines',         sublabel: 'Driving forward',   Icon: Ship,          bg: 'bg-[var(--slate-dim)]',   border: 'border-[var(--slate)]',   text: 'text-[var(--text-sec)]',   badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  anchor:    { label: 'Anchors',         sublabel: 'Holding back',      Icon: Anchor,        bg: 'bg-[var(--coral-dim)]',    border: 'border-[var(--coral)]',    text: 'text-[var(--coral)]',    badge: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
  rock:      { label: 'Rocks',           sublabel: 'Risks ahead',       Icon: AlertTriangle, bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]', badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  preserver: { label: 'Life Preservers', sublabel: 'Risk mitigations',  Icon: LifeBuoy,      bg: 'bg-[var(--green-dim)]',  border: 'border-[var(--green)]',  text: 'text-[var(--green)] dark:text-[var(--text-ter)]',  badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
} as const;

const TYPES: SpeedBoatFactorType[] = ['engine', 'anchor', 'rock', 'preserver'];

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function TranscriptEntry({ event }: { event: GameEvent }) {
  const data = event.eventData as Record<string, unknown>;

  const content = () => {
    switch (event.eventType) {
      case 'join':
        return `${event.participantName} joined${data.isHost ? ' (host)' : ''}`;
      case 'session_started':
        return `${event.participantName} started the session`;
      case 'add_factor': {
        const cfg = TYPE_CONFIG[data.factorType as SpeedBoatFactorType];
        return (
          <>
            <strong>{event.participantName}</strong> added <strong>{data.factorName as string}</strong> as a {cfg.label.slice(0, -1).toLowerCase()}
            {data.note && <div className="text-[var(--text-ter)] text-xs italic mt-0.5">"{data.note as string}"</div>}
          </>
        );
      }
      case 'remove_factor':
        return <><strong>{event.participantName}</strong> removed <strong>{data.factorName as string}</strong></>;
      case 'reclassify_factor': {
        const from = TYPE_CONFIG[data.fromType as SpeedBoatFactorType].label.toLowerCase();
        const to = TYPE_CONFIG[data.toType as SpeedBoatFactorType].label.toLowerCase();
        return (
          <>
            <strong>{event.participantName}</strong> moved <strong>{data.factorName as string}</strong> from {from} → {to}
            {data.note && <div className="text-[var(--text-ter)] text-xs italic mt-0.5">"{data.note as string}"</div>}
          </>
        );
      }
      case 'comment':
        return <><strong>{event.participantName}</strong>: <em>{data.text as string}</em></>;
      case 'done':
        return <><strong>{event.participantName}</strong> finished</>;
      case 'session_ended':
        return 'Session ended';
      default:
        return event.eventType;
    }
  };

  return (
    <div className="flex gap-3 py-2 border-b border-[var(--rule)] last:border-0">
      <span className="text-[var(--text-ter)] text-xs shrink-0 pt-0.5 font-mono">{formatTime(event.createdAt)}</span>
      <span className="text-sm text-[var(--text-sec)]">{content()}</span>
    </div>
  );
}

function exportTranscript(game: UseSpeedBoatLiveSessionResult): void {
  const config = game.session?.config as SpeedBoatConfig | undefined;
  const lines: string[] = [
    'Speed Boat — Session Transcript',
    '='.repeat(50),
    ...(config?.topic ? [`Topic: ${config.topic}`, ''] : ['']),
  ];

  for (const event of game.events) {
    const data = event.eventData as Record<string, unknown>;
    const time = new Date(event.createdAt).toLocaleTimeString();
    let line = `[${time}] `;
    switch (event.eventType) {
      case 'join':          line += `${event.participantName} joined${data.isHost ? ' (host)' : ''}`; break;
      case 'add_factor':    line += `${event.participantName} added ${data.factorName} (${data.factorType})${data.note ? ` — "${data.note}"` : ''}`; break;
      case 'remove_factor': line += `${event.participantName} removed ${data.factorName}`; break;
      case 'reclassify_factor': line += `${event.participantName} moved ${data.factorName}: ${data.fromType} → ${data.toType}${data.note ? ` — "${data.note}"` : ''}`; break;
      case 'comment':       line += `${event.participantName}: ${data.text}`; break;
      case 'done':          line += `${event.participantName} finished`; break;
      default:              line += event.eventType;
    }
    lines.push(line);
  }

  lines.push('', '='.repeat(50), 'Final Board', '');
  for (const type of TYPES) {
    const cfg = TYPE_CONFIG[type];
    const typeFactors = game.factors.filter(f => f.type === type);
    lines.push(`${cfg.label} (${typeFactors.length}):`);
    if (typeFactors.length === 0) {
      lines.push('  (none)');
    } else {
      typeFactors.forEach(f => {
        lines.push(`  • ${f.name} [added by ${f.addedBy}]${f.note ? ` — "${f.note}"` : ''}`);
      });
    }
    lines.push('');
  }

  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `speed-boat-${new Date().toISOString().split('T')[0]}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export function SpeedBoatLiveResultsView({ game, onReset }: SpeedBoatLiveResultsViewProps) {
  const config = game.session?.config as SpeedBoatConfig | undefined;

  // Reclassifications are a notable signal — they represent the group negotiating meaning
  const reclassifications = game.events.filter(e => e.eventType === 'reclassify_factor');

  return (
    <div className="flex gap-6 max-w-6xl mx-auto">
      {/* Results column */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-[var(--text-pri)]">Speed Boat Results</h2>
            {config?.topic && <p className="text-sm text-[var(--text-sec)] mt-0.5">{config.topic}</p>}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => exportTranscript(game)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
            >
              <Download className="h-4 w-4" />
              Export transcript
            </button>
            <button
              onClick={onReset}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              New session
            </button>
          </div>
        </div>

        {/* Summary counts */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {TYPES.map(type => {
            const cfg = TYPE_CONFIG[type];
            const Icon = cfg.Icon;
            const count = game.factors.filter(f => f.type === type).length;
            return (
              <div key={type} className={`text-center p-3  ${cfg.bg} border ${cfg.border}`}>
                <Icon className={`h-6 w-6 mx-auto mb-1 ${TYPE_CONFIG[type].text} opacity-70`} />
                <div className={`text-2xl font-bold ${cfg.text}`}>{count}</div>
                <div className={`text-xs ${cfg.text} opacity-70`}>{cfg.label}</div>
              </div>
            );
          })}
        </div>

        {/* Four-quadrant board — final state */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {TYPES.map(type => {
            const cfg = TYPE_CONFIG[type];
            const Icon = cfg.Icon;
            const typeFactors = game.factors.filter(f => f.type === type);

            return (
              <div key={type} className={` border-2 ${cfg.border} ${cfg.bg} p-3`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-4 w-4 ${TYPE_CONFIG[type].text} opacity-70`} />
                  <span className={`font-semibold text-sm ${cfg.text}`}>{cfg.label} ({typeFactors.length})</span>
                </div>
                {typeFactors.length === 0 ? (
                  <p className={`text-xs italic opacity-40 ${cfg.text}`}>Nothing raised</p>
                ) : (
                  <div className="space-y-2">
                    {typeFactors.map(f => (
                      <div key={f.id} className={`p-2  border ${cfg.border} bg-[var(--bg-raised)]/60`}>
                        <div className={`font-medium text-sm ${cfg.text}`}>{f.name}</div>
                        {f.note && <div className={`text-xs italic opacity-70 mt-0.5 ${cfg.text}`}>"{f.note}"</div>}
                        <div className={`text-xs opacity-50 mt-0.5 ${cfg.text}`}>by {f.addedBy}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Reclassifications — the "negotiated meaning" layer */}
        {reclassifications.length > 0 && (
          <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 mb-4">
            <h3 className="font-semibold text-[var(--mustard)] mb-2">Reclassifications — where the group negotiated meaning</h3>
            <div className="space-y-1">
              {reclassifications.map((e, i) => {
                const data = e.eventData as Record<string, unknown>;
                const fromCfg = TYPE_CONFIG[data.fromType as SpeedBoatFactorType];
                const toCfg = TYPE_CONFIG[data.toType as SpeedBoatFactorType];
                return (
                  <div key={i} className="text-sm text-[var(--mustard)]">
                    <strong>{e.participantName}</strong> moved <strong>{data.factorName as string}</strong>{' '}
                    from {fromCfg.label.toLowerCase()} → {toCfg.label.toLowerCase()}
                    {data.note && <span className="italic"> — "{data.note as string}"</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Participants */}
        <div>
          <h3 className="font-semibold text-[var(--text-pri)] mb-2">Participants</h3>
          <div className="flex flex-wrap gap-2">
            {game.participants.map(p => (
              <div key={p.name} className="px-3 py-1.5 bg-[var(--surface)] border border-[var(--rule)] rounded-full text-sm text-[var(--text-sec)]">
                {p.name}{p.isHost ? ' (host)' : ''}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full transcript */}
      <div className="w-72 shrink-0">
        <h3 className="font-semibold text-[var(--text-pri)] mb-3 flex items-center justify-between">
          Full transcript
          <span className="text-xs text-[var(--text-ter)] font-normal">{game.events.length} events</span>
        </h3>
        <div className="bg-[var(--surface)] border border-[var(--rule)] overflow-y-auto max-h-[600px] px-3 py-2">
          {game.events.map(event => <TranscriptEntry key={event.id} event={event} />)}
        </div>
      </div>
    </div>
  );
}
